"use client";

import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useMemo,
	useState,
} from "react";
import { FileText } from "lucide-react";
import { useTranslations } from "next-intl";
import {
	DataField,
	HStack,
	Link,
	RadioCard,
	Stack,
	Text,
} from "@leabankab/component-library";
import { trackGa4Event } from "@/analytics/ga4";
import { DetailList, OfferHeroCard } from "@/components/ui";
import { useCountryConfig } from "@/config/country-context";
import { useCurrency, useOfferIntegrityWarning } from "@/hooks";
import {
	AMOUNT_PLACEHOLDER,
	formatRate,
	toAmount,
	toAmountOrZero,
} from "@/shared/amount";
import { useMonthsToYears } from "@/shared/use-months-to-years";
import {
	LoanStartInformationContext,
	LoanStartInformationOfferType,
} from "../types";

const termsToYears = (months: number | null | undefined) =>
	Math.round(toAmountOrZero(months) / 12);

type LoanStartProps = LoanStartInformationContext & {
	setOffer: Dispatch<SetStateAction<LoanStartInformationOfferType | undefined>>;
};

const LoanStartComponent = ({
	offer,
	counterOffer,
	isOriginalValid,
	setOffer,
}: LoanStartProps) => {
	const t = useTranslations("pages.StartInformationPage");
	const { rules } = useCountryConfig();

	const [selected, setSelected] = useState<LoanStartInformationOfferType>(
		LoanStartInformationOfferType.ORIGINAL
	);
	const visibleCounterOffer = isOriginalValid ? counterOffer : undefined;

	const { format } = useCurrency();
	const formatDuration = useMonthsToYears();

	const onSelect = (option: LoanStartInformationOfferType) => {
		if (option === selected) return;
		setSelected(option);
		setOffer(option);
	};

	useOfferIntegrityWarning(offer, "start-information");

	const formatPerMonth = useCallback(
		(value: number | null | undefined) => {
			const amount = toAmount(value);

			return amount === null
				? AMOUNT_PLACEHOLDER
				: t("list.perMonthValue", { amount: format(amount) });
		},
		[format, t]
	);

	const benefits = useMemo(() => {
		const activeOffer =
			selected === LoanStartInformationOfferType.ORIGINAL
				? offer
				: (visibleCounterOffer ?? offer);

		return [
			// monthlyCost and interestRate live in the purple card for a single
			// offer; with two offers the card hides them, so show them here instead.
			...(visibleCounterOffer
				? [
						{
							label: t("list.monthlyCost"),
							value: formatPerMonth(activeOffer.monthlyCost),
						},
						{
							label: t("list.interestRate"),
							value: formatRate(activeOffer.interestRate),
						},
					]
				: [
						{
							label: t("list.terms"),
							value: formatDuration(offer.terms),
						},
					]),
			{
				label: t("list.setupFee"),
				value: format(activeOffer.startupFee),
			},
			{
				label: t("list.administrationFee"),
				value: formatPerMonth(activeOffer.termFee),
			},
			{
				label: t("list.effectiveInterestRate"),
				value: formatRate(activeOffer.effectiveInterestRate),
			},
		];
	}, [
		offer,
		visibleCounterOffer,
		selected,
		t,
		format,
		formatPerMonth,
		formatDuration,
	]);

	return (
		<>
			<OfferHeroCard>
				<Stack className="relative z-1">
					<Text textStyle="heading-xl" color="text.contrast">
						{format(offer.finalLoanAmount)}
					</Text>
				</Stack>

				{!visibleCounterOffer && (
					<HStack justify="between" className="relative z-1">
						<div className="flex flex-col">
							<Text
								textStyle="body-sm"
								fontWeight="regular"
								color="primary.subtle"
								className="uppercase"
							>
								{t("list.monthlyCost")}
							</Text>
							<Text textStyle="heading-sm" color="text.contrast">
								{formatPerMonth(offer.monthlyCost)}
							</Text>
						</div>
						<div className="flex flex-col">
							<Text
								textStyle="body-sm"
								fontWeight="regular"
								color="primary.subtle"
								className="text-right uppercase"
							>
								{t("list.interestRate")}
							</Text>
							<Text
								textStyle="heading-sm"
								color="text.contrast"
								className="text-right"
							>
								{formatRate(offer.interestRate)}
							</Text>
						</div>
					</HStack>
				)}
			</OfferHeroCard>
			<Stack gap={2}>
				{visibleCounterOffer && (
					<RadioCard.Group
						label={t("cards.heading")}
						value={selected}
						onValueChange={onSelect}
						columns={2}
					>
						<RadioCard.Root value={LoanStartInformationOfferType.ORIGINAL}>
							<RadioCard.Indicator />
							<RadioCard.Content>
								<RadioCard.Label>
									{formatPerMonth(offer.monthlyCost)}
								</RadioCard.Label>
								<RadioCard.Description>
									{t("cards.perYear", { years: termsToYears(offer.terms) })}
								</RadioCard.Description>
							</RadioCard.Content>
						</RadioCard.Root>
						<RadioCard.Root value={LoanStartInformationOfferType.COUNTEROFFER}>
							<RadioCard.Indicator />
							<RadioCard.Content>
								<RadioCard.Label>
									{formatPerMonth(visibleCounterOffer.monthlyCost)}
								</RadioCard.Label>
								<RadioCard.Description>
									{t("cards.perYear", {
										years: termsToYears(visibleCounterOffer.terms),
									})}
								</RadioCard.Description>
							</RadioCard.Content>
						</RadioCard.Root>
					</RadioCard.Group>
				)}
				<DetailList>
					{benefits.map((row, index) => (
						<DataField
							key={row.label}
							label={row.label}
							value={row.value}
							divider={index !== benefits.length - 1}
						/>
					))}
				</DetailList>

				{rules.secciDocument && offer.secciDocument && (
					<Link
						href={offer.secciDocument}
						target="_blank"
						rel="noopener noreferrer"
						onClick={() =>
							trackGa4Event("signing_document_opened", {
								product_type: "loan",
							})
						}
						className="flex gap-1"
					>
						<FileText
							size={18}
							className="mt-0.5 shrink-0"
							aria-hidden="true"
						/>
						<span>{t("secci.downloadText")}</span>
					</Link>
				)}
			</Stack>
		</>
	);
};

export default LoanStartComponent;
